<template>
    <!-- Step Description -->
    <div class="step-description">{{t('join.createAccount.step6.description')}}</div>
    <!-- *********************************************
    *                       Form                     *
    ********************************************** -->
    <input-search
    preventInputClick
    :params="{ placeholder: t('join.createAccount.step6.searchPlaceholder') }"
    @clicked="showMobileSearch = true"/>
    <!-- Select Branch -->
    <div v-if="selectedBranch" class="select-branch-wrapper prevent-auto-margins">
        <!-- Label -->
        <label-generic :text="t('join.createAccount.step6.selectedBranch')"/>
        <!-- Selected Agency Table -->
        <table-generic
        :hasDetail="false"
        :headerData="selectedTableHeaderData"
        :tableStyles="selectedTableStyles"
        :tableData="selectedTableData"
        @rowClicked="showMobileSearch = true"/>
    </div>
    <!-- *********************************************
    *               Search Modal for Mobile          *
    ********************************************** -->
    <modal-side-search
    v-if="showMobileSearch"
    :searchPlaceholder="t('join.createAccount.step6.searchPlaceholder')"
    :searchInputValue="searchValue"
    :applyEmptyState="!filteredAgenciesList.length && !filterTags.length"
    :triggerClose="triggerMobileSearchClose"
    @scrollValue="expandedFloatingButton = $event === 0"
    @updateSearchValue="searchValue = $event"
    @hideMe="[showMobileSearch = false, triggerMobileSearchClose = false, searchValue = '']">
        <!-- *********************************************
        *                    Filter Tags                 *
        ********************************************** -->
        <chip-wrapper
        v-if="filterTags.length"
        :style="{'padding-left': '16px'}"
        :filters="filterTags"
        @close="removeFilterHandler"
        @closeAll="closeAllFiltersHandler"/>
        <!-- Table Generic -->
        <table-generic
        :hasDetail="false"
        :headerData="tableHeaderData"
        :tableStyles="tableStyles"
        :tableData="tableData"
        @rowClicked="tableClickHandler"/>
        <!-- Floating Button -->
        <button-floating
        icon="filter"
        :extended="expandedFloatingButton"
        :label="t('join.createAccount.step6.filtersButton')"
        @clicked="showFilterModal = true"/>
    </modal-side-search>
    <!-- Agencies Filters -->
	<agencies-filter-modal
	v-if="showFilterModal"
	:appliedFilters="appliedFilters"
	@hideMe="showFilterModal = false"
	@applyFilters="applyFiltersHandler($event)"/>
    <!-- Feedback Toasts -->
    <toasts-generic
	v-if="showSelectAgencyToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
    :triggerClose="triggerCloseToast"
	@hideMe="showSelectAgencyToast = false"/>
</template>

<script setup>
import InputSearch from '@/components/generic/inputs/Search'
import LabelGeneric from '@/components/generic/labels/Generic'
import ModalSideSearch from '@/components/generic/modals/side/Search'
import ButtonFloating from '@/components/generic/buttons/Floating'
import ChipWrapper from '@/components/generic/buttons/chip/ChipWrapper'
import TableGeneric from '@/components/generic/table/Generic'
import AgenciesFilterModal from '@/components/features/nonAuthenticated/join/createAccount/step6Modals/AgenciesFilterModal'
import ToastsGeneric from '@/components/generic/toasts/Generic'

/* Vue & Helpers */
import { ref, computed, watch } from 'vue'
import { moveElementInArray } from '@/assets/scripts/arrays'
import { errorObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const emit = defineEmits([
    'updateFormData',
    'resetShowMandatoryErrorsProp'
])

/** Props
@formData {Object} - Object with form data
@mandatoryList {Array} - List of mandatory fields ids/keys
@showMandatoryErrors {Boolean} - Prop to trigger display of inputs errors
@clearFormErrors {Boolean} - Boolean controller to clear form and errors
*/
const props = defineProps({
    formData: Object,
    mandatoryList: Array,
    showMandatoryErrors: Boolean,
    clearFormErrors: Boolean
})

const { getAgenciesProvinces, getBranches } = mapGetters(moduleMapper.JOIN_CREATE_ACCOUNT)

/* Data */
const showMobileSearch = ref(false)
const triggerMobileSearchClose = ref(false)
const selectedBranch = ref(getBranches.value.find(obj => obj.id === props.formData.branchId))

/**********************************************************************
*                                                                     *
*                        Selected Table Data                          *
*                                                                     *
**********************************************************************/
const selectedTableHeaderData = [
    { label: '', dataAttr: 'map-icon', showInMobile: true, rowCellType: 'agency-pin' },
	{ label: '', showInMobile: { groupWith: 'address' }, dataAttr: 'name', rowCellType: 'title' },
    { label: '', dataAttr: 'address', rowCellType: 'description' },
    { label: '', dataAttr: 'checked', rowCellType: 'icon', showInMobile: true }
]

const selectedTableStyles = [null, null, null, null]

const selectedTableData = computed(() => {
    if (!selectedBranch.value) return []

    return [[
        { value: selectedBranch.value.id, dataAttr: 'id' },
        {
            type: 'agency-pin',
            value: { icon: 'map-pin-agency', agencyName: '' },
            dataAttr: 'map-icon'
        },
        { type: 'title', value: selectedBranch.value.name, dataAttr: 'name' },
        {
            type: 'description',
            value: `${selectedBranch.value.address}, ${selectedBranch.value.city.toLowerCase().charAt(0).toUpperCase() + selectedBranch.value.city.toLowerCase().slice(1)}`,
            dataAttr: 'address'
        },
        {
            type: 'icon',
            value: 'check-line',
            dataAttr: 'checked'
        }
    ]]
})

/**********************************************************************
*                                                                     *
*                            Table Data                               *
*                                                                     *
**********************************************************************/
const tableHeaderData = [
    { label: '', dataAttr: 'map-icon', showInMobile: true, rowCellType: 'agency-pin' },
	{ label: '', showInMobile: { groupWith: 'address' }, dataAttr: 'name', rowCellType: 'title' },
    { label: '', dataAttr: 'address', rowCellType: 'description' },
    { label: '', dataAttr: 'checked', rowCellType: 'icon', showInMobile: true }
]

const tableStyles = [null, null, null, null]

const tableData = computed(() => {
    const agenciesList = filteredAgenciesList.value

    return agenciesList.map(agency => {
		return [
            { value: agency.id, dataAttr: 'id' },
            {
                type: 'agency-pin',
                value: { icon: 'map-pin-agency', agencyName: '' },
                dataAttr: 'map-icon'
            },
            { type: 'title', value: agency.name, dataAttr: 'name' },
            {
                type: 'description',
                value: `${agency.address}, ${agency.city.toLowerCase().charAt(0).toUpperCase() + agency.city.toLowerCase().slice(1)}`,
                dataAttr: 'address'
            },
            {
                type: 'icon',
                value: selectedBranch.value && selectedBranch.value.id === agency.id ? 'check-line' : null,
                dataAttr: 'checked'
            }
        ]
	})
})

const tableClickHandler = $event => {
    selectedBranch.value = getBranches.value.find(obj => obj.id === $event.rowId)
    triggerMobileSearchClose.value = true
    triggerCloseToast.value = true
    emit('updateFormData', { field: 'branchId', inputValue: selectedBranch.value.id })
}

/**********************************************************************
*                                                                     *
*                                Filters                              *
*                                                                     *
**********************************************************************/
const searchValue = ref('')
const appliedFilters = ref({ agenciesProvince: '' }) // Im letting this be a object in case in the future we have more than one filter
const expandedFloatingButton = ref(true)
const showFilterModal = ref(false)
const filterTags = ref([])

const sortedBranches = computed(() => {
    if (!selectedBranch.value) return getBranches.value
    const selectedBranchIndex = getBranches.value.findIndex(obj => obj.id === selectedBranch.value.id)
    /* We need to deconstruct getBranches.value to prevent binds from happening */
    return moveElementInArray(JSON.parse(JSON.stringify(getBranches.value)), selectedBranchIndex, 0)
})

const filteredAgenciesList = computed(() => {
    /* If no filter is applied we simply return all agencies */
    if (!appliedFilters.value.agenciesProvince.length && !searchValue.value.length) return sortedBranches.value

	let agenciesList = sortedBranches.value
    if (appliedFilters.value.agenciesProvince.length) {
        agenciesList = sortedBranches.value.filter(ele => ele.district.toLowerCase() === appliedFilters.value.agenciesProvince.toLowerCase())
    }

    return agenciesList.reduce((acc, agency) => {
        const searchValueLowered = searchValue.value.toLowerCase()

        const searchByName = agency.name.toLowerCase().includes(searchValueLowered)
        const searchByAddress = agency.address.toLowerCase().includes(searchValueLowered)

        if (searchByName || searchByAddress) acc.push(agency)
        return acc
    }, [])
})

const applyFiltersHandler = (filters) => {
	filterTags.value = []

	for (const filter in filters) {
		if (filters[filter]) {
			appliedFilters.value[filter] = filters[filter]

			if (filterTags.value.find(tag => { return tag.id === filter })) {
                filterTags.value.find(tag => { return tag.id === filter }).text = filterTagTextHandler(filter, filters[filter])
			} else {
				filterTags.value.push({
					id: filter,
					label: t(`join.createAccount.step6.filters.${filter}`),
					text: filterTagTextHandler(filter, filters[filter])
				})
			}
		} else {
			appliedFilters.value[filter] = ''
		}
	}
}

const filterTagTextHandler = (filter, value) => {
	switch (filter) {
		case 'agenciesProvince':
			return getAgenciesProvinces.value.find(type => type.id === value).text
		default: return value
	}
}

const removeFilterHandler = (tagId) => {
	appliedFilters.value[tagId] = ''

	filterTags.value = filterTags.value.filter(filter => filter.id !== tagId)

    if (!filterTags.value.length) filterTags.value = []
}

const closeAllFiltersHandler = () => {
	filterTags.value = []

	for (const filter in appliedFilters.value) {
		appliedFilters.value[filter] = ''
    }
}

/**********************************************************************
*                                                                     *
*                          Mandatory Fields                           *
*                                                                     *
***********************************************************************/
const showSelectAgencyToast = ref(false)
const triggerCloseToast = ref(false)
const feedbackToastData = ref(errorObjectConstructor({
    message: t('join.createAccount.step6.mandatoryBranch'),
    dismissToastBtnText: t('commonButtons.gotIt')
}))

watch(() => props.showMandatoryErrors, newValue => {
	if (newValue) showSelectAgencyToast.value = true
    emit('resetShowMandatoryErrorsProp')
})
</script>

<style lang="scss" scoped>
.step-description {
    @include H6-Regular;
    color: var(--secondary-dark-2);
    line-height: 18px;
}

.select-branch-wrapper {
    label {
        width: calc(100% - 32px);
        margin: 0 16px;
    }

    :deep(.data-table-row.mobile) td {
        border-bottom: unset;
    }
}

.button-floating {
    position: fixed;
    bottom: 16px;
    right: 16px;
}
</style>
